import React, { useEffect, useState } from 'react';
import { API } from '../api';
import SidePanel from '../ui/SidePanel';
import Button from '../ui/Button';
import { Field, RadioRows, Select, controlClass } from '../ui/Field';
import { useToast } from '../ui/Toast';
import { plural } from '../ui/format';

const SEVERITY_CHOICES = [
  { value: '', label: 'All' },
  { value: 'Critical', label: 'Critical' },
  { value: 'High', label: 'High and above' },
  { value: 'Medium', label: 'Medium and above' },
  { value: 'Low', label: 'Low and above' },
];
const MODE_CHOICES = [
  { value: 'preview', label: 'Preview', description: 'Saved here only. Review them, then open them in Jotform.' },
  { value: 'normal', label: 'Normal', description: 'Opens the tickets in Jotform right away.' },
];

/**
 * Side panel that creates container image tickets: one ticket per image, over its
 * findings that are not in an open ticket yet.
 * images: [{ id, name }] for "all findings of these images";
 * findingIds (with images = the one image) for "only these findings".
 * onCreated({ created, ticketIds }) runs after the request succeeds; the panel shows the toast itself.
 */
export default function ImageTicketsPanel({ open, onClose, onCreated, images = [], findingIds = [] }) {
  const toast = useToast();
  const [defaultAssignee, setDefaultAssignee] = useState('');
  const [form, setForm] = useState({ assignee: '', severity: '', mode: 'preview' });
  const [error, setError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (key) => (value) => setForm((prev) => ({ ...prev, [key]: value }));
  const selectedFindings = findingIds.length > 0;

  useEffect(() => {
    if (!open) return undefined;
    setError(null);
    setSubmitError(null);
    let current = true;
    API.GET_IMAGE_TICKET_DEFAULTS().then(
      (result) => {
        if (!current) return;
        const value = result?.default_assignee || '';
        setDefaultAssignee(value);
        setForm((prev) => (prev.assignee ? prev : { ...prev, assignee: value }));
      },
      () => {},
    );
    return () => {
      current = false;
    };
  }, [open]);

  const submit = async (event) => {
    event.preventDefault();
    setSubmitError(null);
    if (!form.assignee.trim()) {
      setError('Enter an assignee. No default assignee is configured on the server.');
      document.getElementById('image-tickets-assignee')?.focus();
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const result = await API.CREATE_IMAGE_TICKETS({
        image_ids: selectedFindings ? [] : images.map((i) => i.id),
        finding_ids: findingIds,
        assignee: form.assignee.trim(),
        severity: selectedFindings ? undefined : form.severity || undefined,
        preview_mode: form.mode === 'preview',
      });
      const created = result?.tickets_created || 0;
      const message = result?.message || (created > 0 ? `Created ${plural(created, 'ticket')}.` : 'No tickets were created.');
      if (created > 0) toast.success(message);
      else toast.info(message);
      onCreated?.({ created, ticketIds: result?.ticket_ids || [] });
      onClose();
    } catch (e) {
      setSubmitError(e.message || 'The request did not complete.');
    } finally {
      setBusy(false);
    }
  };

  const scope = selectedFindings
    ? `${plural(findingIds.length, 'selected finding')} on ${images[0]?.name || 'this container image'}`
    : images.length === 1 ? images[0].name : plural(images.length, 'container image');

  return (
    <SidePanel
      open={open}
      onClose={busy ? () => {} : onClose}
      title="Create tickets"
      description={selectedFindings
        ? 'One ticket with the selected findings that are not in an open ticket yet.'
        : 'One ticket per container image, with its findings that are not in an open ticket yet.'}
      footer={(
        <>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button type="submit" form="image-tickets-form" variant="primary" disabled={busy}>
            {busy ? 'Creating tickets…' : 'Create tickets'}
          </Button>
        </>
      )}
    >
      <form id="image-tickets-form" onSubmit={submit} noValidate className="space-y-5">
        <div>
          <p className="mb-1 text-small font-semibold text-ink">{selectedFindings ? 'Findings' : images.length === 1 ? 'Container image' : 'Container images'}</p>
          <p className={`text-id font-semibold text-ink break-all ${selectedFindings || images.length > 1 ? '' : 'font-mono'}`}>{scope}</p>
          {!selectedFindings && images.length > 1 && (
            <p className="mt-1 font-mono text-small text-graphite break-all">{images.map((i) => i.name).join(', ')}</p>
          )}
        </div>

        <Field
          id="image-tickets-assignee"
          label="Assignee"
          hint={defaultAssignee ? `Default: ${defaultAssignee}` : 'Name as it appears in Jotform.'}
          error={error}
        >
          <input
            id="image-tickets-assignee"
            type="text"
            value={form.assignee}
            onChange={(e) => set('assignee')(e.target.value)}
            autoComplete="off"
            disabled={busy}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? 'image-tickets-assignee-error' : 'image-tickets-assignee-hint'}
            className={controlClass({ invalid: Boolean(error) })}
          />
        </Field>

        {!selectedFindings && (
          <Field id="image-tickets-severity" label="Severity" hint="Findings rated Unknown are left out when a severity is set.">
            <Select id="image-tickets-severity" options={SEVERITY_CHOICES} value={form.severity} onChange={(e) => set('severity')(e.target.value)} disabled={busy} />
          </Field>
        )}

        <RadioRows name="image-tickets-mode" label="Mode" options={MODE_CHOICES} value={form.mode} onChange={set('mode')} disabled={busy} />

        {submitError && (
          <div role="alert" className="px-3 py-2.5 rounded-control border border-crit/40 bg-crit-tint">
            <p className="text-body font-semibold text-crit">Tickets could not be created</p>
            <p className="mt-1 font-mono text-small text-ink break-words">{submitError}</p>
          </div>
        )}
      </form>
    </SidePanel>
  );
}
